import { sumByProp } from 'common/utils/ramda';
import { getTaxes } from 'common/assets/tax';
import { isExist, getTimestamp, calcSum, round } from 'common/utils';
import { auth, db } from 'firebase/config';
import { setDocWithCreatedBy } from 'firebase/utils';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  setDoc,
  getDocs,
  getDoc,
  query,
  where,
  deleteDoc,
  CollectionReference,
  DocumentData,
  runTransaction,
  Transaction,
  DocumentSnapshot,
} from 'firebase/firestore';
import {
  Action,
  AppState,
  ClosedOrder,
  ClosedOrderItem,
  InventoryItem,
  Item,
  Optional,
  Order,
  OrderItem,
  OrderStatuses,
  TaxRecord,
  AppHelpers
} from 'common/types';
import {
  createOrder,
  getOrderEntity,
  getOrderIndexById,
  getOrderItemEntity,
  getOrderItemIndexById,
} from 'common/assets/order';

const ordersCollection: CollectionReference<DocumentData> = collection(db, 'orders');
const closedOrdersCollection: CollectionReference<DocumentData> = collection(db, 'closedOrders');
const stockMovementsCollection: CollectionReference<DocumentData> = collection(db, 'stockMovements');
const inventoryItemsCollection: CollectionReference<DocumentData> = collection(db, 'inventoryItems');

export type OrderChargeAction = Pick<
  Optional<ClosedOrder, 'dateClose'>,
  | 'cardPaymentAmount'
  | 'cashChange'
  | 'cashPaymentAmount'
  | 'closingReason'
  | 'customerId'
  | 'dateClose'
  | 'isDiscounted'
  | 'totalPaymentAmount'
>;

export type OrdersActions = {
  add: () => Promise<Order>;
  addItem: (product: Item, orderId?: string) => Promise<void>;
  charge: (data: OrderChargeAction, orderId: string) => Promise<ClosedOrder>;
  select: (orderId: string) => void;
  updateSelected: (order: Order) => Promise<void>;
  findById: (orderId: string) => Promise<Order | null>;
  cleanCurrentOrder: () => void;
  cancelOrder: (orderId: string, reason: string) => Promise<void>;
};

function update(order: Order, state: AppState): Order {
  const updOrder = { ...order };
  const appliedTaxes: TaxRecord[] = [];
  const updOrderItems: OrderItem[] = [];

  // recalculates the order items amounts
  updOrder.items.forEach((orderItem) => {
    const item = state.items.find((entity) => entity.id === orderItem.id);
    if (!item) return;
    const { quantity, price } = orderItem;
    const taxes = getTaxes(item, state);
    const updOrderItem = { ...orderItem };
    updOrderItem.amount = quantity * price;
    updOrderItem.taxAmount = calcSum(taxes, 'taxAmount') * quantity;
    updOrderItem.includedTaxAmount = calcSum(taxes, 'includedTaxAmount') * quantity;
    updOrderItem.totalTaxAmount = updOrderItem.taxAmount + updOrderItem.includedTaxAmount;
    updOrderItem.taxes = taxes;
    taxes.forEach((tax) =>
      appliedTaxes.push({
        ...tax,
        taxAmount: tax.taxAmount * quantity,
        includedTaxAmount: tax.includedTaxAmount * quantity,
      }),
    );
    updOrderItems.push(updOrderItem);
  });

  updOrder.items = updOrderItems;

  // recalculates the order items amounts
  const unquieTaxIds: string[] = Array.from(new Set(appliedTaxes.map((item) => item.id)));
  const orderTaxes = unquieTaxIds.map((taxId: string) => {
    const taxList = appliedTaxes.filter((entity) => entity.id === taxId);
    return {
      id: taxId,
      includedTaxAmount: calcSum(taxList, 'includedTaxAmount'),
      taxAmount: calcSum(taxList, 'taxAmount'),
    };
  });

  updOrder.appliedTaxes = orderTaxes;
  updOrder.taxAmount = calcSum(updOrder.items, 'taxAmount');
  updOrder.taxRoundedAmount = round(updOrder.taxAmount);
  updOrder.subTotalAmount = calcSum(updOrder.items, 'amount');
  updOrder.subTotalRoundedAmount = round(updOrder.subTotalAmount);
  updOrder.totalAmount = updOrder.taxAmount + updOrder.subTotalAmount;
  updOrder.totalRoundedAmount = updOrder.taxRoundedAmount + updOrder.subTotalRoundedAmount;
  updOrder.totalPaymentAmount = updOrder.cardPaymentAmount + updOrder.cashPaymentAmount;
  updOrder.dateUpdated = getTimestamp(); // update the last modification time

  return updOrder;
}

export const createOrdersActions: Action<OrdersActions> = (state, updateState, helpers) => ({
  // Adds the selected item to the current order
  addItem: async (orderItem, orderId = undefined) => {
    updateState((prevState) => {
      const updOrders = [...prevState.orders];
      let orderIndex = getOrderIndexById(updOrders, orderId || prevState.currentOrderId);

      // If the order does not exist create a new one
      if (!isExist(orderIndex)) {
        const [, newOrder] = createOrder(prevState);
        updOrders.push(newOrder);
        orderIndex = updOrders.length - 1;
      }

      // Get the order to update
      const updOrder = { ...updOrders[orderIndex], items: [...updOrders[orderIndex].items] };
      updOrders[orderIndex] = updOrder;

      // If the order is closed, create a new one
      if (updOrder.status === OrderStatuses.Closed) {
        const [, newOrder] = createOrder(prevState);
        updOrders.push(newOrder);
        orderIndex = updOrders.length - 1;
      }

      // Find if the item already exists in the order
      const orderItemIndex = getOrderItemIndexById(updOrder.items, orderItem.id);

      // If the item exists, update the quantity
      if (isExist(orderItemIndex)) {
        updOrder.items[orderItemIndex] = { ...updOrder.items[orderItemIndex], quantity: updOrder.items[orderItemIndex].quantity + 1 };
      } else {
        // Otherwise, add the item to the order
        const orderItemEntity = getOrderItemEntity(orderItem);
        updOrder.items.push(orderItemEntity);
      }

      Object.assign(updOrder, update(updOrder, prevState));

      return { orders: updOrders, currentOrderId: updOrder.id, currentItemId: null };
    }, false);
  },

  // Order Closing
  charge: async (data, orderId) => {
    const { translation } = helpers || {};
    const t = translation?.t || (() => '');
    const order = state.orders.find((order) => order.id === orderId);

    // Array para almacenar todos los movimientos de stock creados durante el proceso
    const createdStockMovements: any[] = [];

    if (!order) throw new Error('The specified order does not exist');

    const closedOrderItems: ClosedOrderItem[] = order.items.map((entity) => {
      const costPrice = state.items.find((item) => item.id === entity.id)?.costPrice || 0;
      const amount = entity.quantity * entity.price;
      const item: ClosedOrderItem = {
        ...entity,
        costPrice,
        amount,
        profit: amount - entity.quantity * costPrice,
        roundedAmount: round(entity.quantity * entity.price),
        isWeighing: false,
        isNonDiscounted: false,
      };
      return item;
    });

    const closedOrder: ClosedOrder = {
      ...order,
      ...data,
      items: closedOrderItems,
      dateUpdated: getTimestamp(),
      dateClose: data.dateClose || getTimestamp(),
      profit: sumByProp('profit', closedOrderItems),
      totalRoundedAmount: sumByProp('roundedAmount', closedOrderItems),
    };

    // Add to Firestore closedOrders collection
    // await addDoc(closedOrdersCollection, closedOrder);
    let orderData;
    if (!order.isExternal) {
      orderData = await setDocWithCreatedBy(ordersCollection, order);
    }
    // Add to Firestore closedOrders collection
    const closedOrderData = await setDocWithCreatedBy(closedOrdersCollection, closedOrder);

    // Update original order status in Firestore
    // const orderRef = doc(ordersCollection, orderId);
    const updatedOrder = {
      ...orderData || order,
      status: OrderStatuses.Closed,
      dateClose: getTimestamp(),
      dateUpdated: getTimestamp(),
    } as Order;
    // await updateDoc(orderRef, {...updatedOrder, createdBy: auth.currentUser?.uid});

    // Update inventory based on sold items
    try {
      console.log('Procesando venta para actualizar inventario:', order.items);
      // Process each item in the order to update inventory
      for (const item of order.items) {
        // Create a stock movement for each item sold
        const movement = {
          inventoryItemId: item.id,
          quantity: -1 * item.quantity, // Negative quantity for sale
          type: 'sale' as 'purchase' | 'consumption' | 'sale' | 'adjustment',
          notes: `Sale from order ${orderId}`,
          reference: orderId,
          ownerId: state.currentUserId || undefined,
          parentUserId: state.parentId || undefined
        };

        // Buscar el ítem de inventario directamente por ID o por associatedProductId
        const inventoryItem = state.inventoryItems?.find(invItem => 
          invItem.id === item.id || 
          (invItem.associatedProductId && invItem.associatedProductId === item.id)
        );

        if (inventoryItem) {
          // Ajustar el movimiento para usar el ID del ítem de inventario (no del producto)
          const adjustedMovement = {
            ...movement,
            inventoryItemId: inventoryItem.id,
            id: '',
            date: getTimestamp()
          };

          console.log('Item encontrado en inventario, creando movimiento:', adjustedMovement);
          
          // Crear el movimiento de stock
          const createdMovement = await setDocWithCreatedBy(stockMovementsCollection, adjustedMovement);
          console.log('Movimiento creado:', createdMovement);

          // Guardar el movimiento para actualizar el estado al final del proceso
          createdStockMovements.push(createdMovement);

          // Actualizar la cantidad del ítem en el inventario
          const updatedItem = {
            ...inventoryItem,
            quantity: inventoryItem.quantity + (-1 * item.quantity), // Restar la cantidad vendida
            lastModifiedTime: getTimestamp()
          };

          // Actualizar en Firestore
          const itemRef = doc(collection(db, 'inventoryItems'), inventoryItem.id);
          await updateDoc(itemRef, {
            quantity: updatedItem.quantity,
            lastModifiedTime: updatedItem.lastModifiedTime
          });
          console.log('Item de inventario actualizado:', updatedItem);
        } else {
          // Check if there's a recipe for this product
          const recipe = state.recipes?.find(r => r.productId === item.id);
          if (recipe) {
            console.log('Receta encontrada para el producto:', recipe);
            // Process each ingredient in the recipe
            for (const ingredient of recipe.ingredients) {
              // Obtener el nombre del producto vendido
              const productItem = state.items.find(i => i.id === item.id);
              const productName = productItem ? productItem.name : item.id;

              const ingredientMovement = {
                inventoryItemId: ingredient.inventoryItemId,
                quantity: -1 * ingredient.quantity * item.quantity, // Negative quantity for consumption
                type: 'sale' as 'purchase' | 'consumption' | 'sale' | 'adjustment',
                notes: `${t('admin.inventory.consumedInSale', { productName, orderId })}`,
                reference: orderId,
                ownerId: state.currentUserId || undefined,
                parentUserId: state.parentId || undefined
              };

              console.log('Creando movimiento para ingrediente:', ingredientMovement);
              // Add the ingredient movement
              const createdIngredientMovement = await setDocWithCreatedBy(stockMovementsCollection, {
                ...ingredientMovement,
                id: '',
                date: getTimestamp()
              });
              console.log('Movimiento de ingrediente creado:', createdIngredientMovement);

              // Guardar el movimiento para actualizar el estado al final del proceso
              createdStockMovements.push(createdIngredientMovement);

              // También actualizar la cantidad del ingrediente en el inventario
              const inventoryItem = state.inventoryItems?.find(invItem => invItem.id === ingredient.inventoryItemId);

              // console.log('BOTH QUANTITY:',{
              //   id: inventoryItem?.id,
              //   quantity: inventoryItem?.quantity || 0,
              //   ingredientMovementQuantity: ingredientMovement.quantity,
              //   resta: (inventoryItem?.quantity || 0) + ingredientMovement.quantity
              // });
              if (inventoryItem) {
                const updatedItem = {
                  ...inventoryItem,
                  quantity: (inventoryItem.quantity || 0) + ingredientMovement.quantity, // Restar la cantidad consumida
                  lastModifiedTime: getTimestamp()
                };

                // Actualizar en Firestore
                const itemRef = doc(collection(db, 'inventoryItems'), inventoryItem.id);
                // console.log('ITEM ID', inventoryItem.id);
                // console.log('QUANTITY', updatedItem.quantity);
                await updateDoc(itemRef, {
                  quantity: updatedItem.quantity,
                  lastModifiedTime: updatedItem.lastModifiedTime

                });
                console.log('Ingrediente actualizado en inventario:', updatedItem);
              }
            }
          } else {
            console.log('Producto no encontrado en inventario y sin receta asociada:', item.id);
          }
        }
      }
    } catch (error) {
      console.error('Error updating inventory:', error);
      // Continue with the order process even if inventory update fails
    }

    // Recopilar todos los elementos de inventario actualizados durante el proceso
    const updatedInventoryItems: InventoryItem[] = [];

    // Actualizar el inventario local con los cambios realizados en Firestore
    for (const item of order.items) {
      // Caso 1: El producto es un ítem de inventario directo
      const inventoryItem = state.inventoryItems?.find(invItem => invItem.id === item.id);
      if (inventoryItem) {
        const updatedItem = {
          ...inventoryItem,
          quantity: inventoryItem.quantity + (-1 * item.quantity),
          lastModifiedTime: getTimestamp()
        };
        updatedInventoryItems.push(updatedItem);
      } else {
        // Caso 1B: El producto está asociado a un ítem de inventario (nuevo caso)
        const associatedInventoryItem = state.inventoryItems?.find(invItem => 
          invItem.associatedProductId === item.id
        );
        
        if (associatedInventoryItem) {
          const updatedAssociatedItem = {
            ...associatedInventoryItem,
            quantity: associatedInventoryItem.quantity + (-1 * item.quantity),
            lastModifiedTime: getTimestamp()
          };
          updatedInventoryItems.push(updatedAssociatedItem);
          console.log('Estado local actualizado para ítem de inventario asociado:', updatedAssociatedItem);
        } else {
          // Caso 2: El producto tiene una receta con ingredientes
          const recipe = state.recipes?.find(r => r.productId === item.id);
          if (recipe) {
            for (const ingredient of recipe.ingredients) {
              const ingredientItem = state.inventoryItems?.find(invItem => invItem.id === ingredient.inventoryItemId);
              if (ingredientItem) {
                const updatedIngredientItem = {
                  ...ingredientItem,
                  quantity: (ingredientItem.quantity || 0) + (-1 * ingredient.quantity * item.quantity),
                  lastModifiedTime: getTimestamp()
                };
                updatedInventoryItems.push(updatedIngredientItem);
              }
            }
          }
        }
      }
    }

    // Actualizar el estado de orders
    const updOrders = [
      ...state.orders.filter((order) => order.id !== orderId),
      updatedOrder,
    ];
    const updClosedOrders: ClosedOrder[] = [closedOrderData, ...state.closedOrders];

    // Combinar los movimientos de stock existentes con los nuevos creados
    const allStockMovements = [...(state.stockMovements || []), ...createdStockMovements];

    // Actualizar el estado con los nuevos orders y los items de inventario actualizados
    if (updatedInventoryItems.length > 0) {
      // Crear una copia del inventario actual
      const currentInventory = [...(state.inventoryItems || [])];

      // Actualizar cada ítem modificado
      updatedInventoryItems.forEach(updatedItem => {
        const index = currentInventory.findIndex(item => item.id === updatedItem.id);
        if (index !== -1) {
          currentInventory[index] = updatedItem;
        }
      });

      // Actualizar el estado con todo junto
      updateState({
        orders: updOrders,
        closedOrders: updClosedOrders,
        currentItemId: null,
        currentOrderId: null,
        inventoryItems: currentInventory,
        stockMovements: allStockMovements
      });
    } else {
      // Si no hay cambios en el inventario, actualizar solo los orders y los movimientos de stock
      updateState({
        orders: updOrders,
        closedOrders: updClosedOrders,
        currentItemId: null,
        currentOrderId: null,
        stockMovements: allStockMovements
      });
    }
    return closedOrder;
  },

  cleanCurrentOrder: () => updateState({ currentOrderId: null, currentItemId: null }),

  // Updates current order data
  updateSelected: async (order) => {
    console.log(order);
    const updOrders = [...state.orders];
    const currentOrderIndex = updOrders.findIndex((order) => state.currentOrderId === order.id);

    // if the order exists just update it
    if (isExist(currentOrderIndex)) {
      const updOrder: Order = { ...order };
      updOrders[currentOrderIndex] = update(updOrder, state);

      // Update in Firestore
      // if (order.isExternal) {
      //   const orderRef = doc(ordersCollection, updOrder.id);
      //   await updateDoc(orderRef, updOrder);
      // }
    }

    updateState({ orders: updOrders, currentItemId: null });
  },

  add: async () => {
    const [updOrders, updOrder] = createOrder(state);

    // Add to Firestore
    // await setDocWithCreatedBy(ordersCollection, updOrder);

    updateState({ orders: updOrders, currentOrderId: updOrder.id });
    return updOrder;
  },

  select: (orderId: string | null) => updateState({ currentOrderId: orderId }),

  findById: async (orderId: string): Promise<Order | null> => {
    // First check local state
    const localOrder = state.orders.find((entity) => orderId === entity.id);
    if (localOrder) return localOrder;

    // If not found locally, try Firestore
    // const orderRef = doc(ordersCollection, orderId);
    // const orderDoc = await getDoc(orderRef);
    // if (!orderDoc.exists()) return null;

    // return orderDoc.data() as Order;
    return null; // Si no está en el estado local, retornamos null ya que Firestore está comentado
  },
  
  cancelOrder: async (orderId: string, reason: string): Promise<void> => {
    // Buscar la orden cerrada en el estado
    const orderIndex = state.closedOrders.findIndex(order => order.id === orderId);
    if (orderIndex === -1) {
      throw new Error('Orden no encontrada');
    }
    
    // Obtener usuario actual
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No autorizado');
    }
    
    // Crear una copia de la orden con los datos de cancelación
    const order = state.closedOrders[orderIndex];
    const updatedOrder = {
      ...order,
      isCancelled: true,
      cancelledBy: currentUser.uid,
      cancelledAt: getTimestamp(),
      cancellationReason: reason
    };
    
    try {
      // 1. Actualizar la orden a estado cancelado
      const orderRef = doc(closedOrdersCollection, orderId);
      const orderDoc = await getDoc(orderRef);
      
      if (!orderDoc.exists()) {
        throw new Error('Orden no encontrada en Firestore');
      }
      
      // Actualizar orden con datos de cancelación
      await updateDoc(orderRef, {
        isCancelled: true,
        cancelledBy: currentUser.uid,
        cancelledAt: getTimestamp(),
        cancellationReason: reason
      });
      
      // 2. Preparar estructuras para seguimiento de cambios
      const inventoryUpdates: Array<any> = [];
      const createdStockMovements: any[] = [];
      
      console.log('Iniciando restauración de stock para orden cancelada:', orderId);
      console.log('Cantidad de ítems a procesar:', order.items.length);
      console.log('Estado actual de inventario:', state.inventoryItems?.length || 0, 'items');
      console.log('Estado actual de movimientos:', state.stockMovements?.length || 0, 'movimientos');
      
      // 3. Procesar cada ítem para restaurar stock
      for (const item of order.items) {
        const quantityToRestore = item.quantity;
        
        // Buscar el ítem de inventario asociado con este producto
        // Primero intentamos encontrar un ítem en el inventario cuyo associatedProductId sea igual al item.id
        let inventoryItem = null;
        
        // Buscamos en el estado actual si existe un item con associatedProductId igual al item.id
        if (state.inventoryItems) {
          inventoryItem = state.inventoryItems.find(invItem => invItem.associatedProductId === item.id);
        }
        
        // Obtenemos la referencia del inventario por su ID (si existe) o creamos una nueva
        const inventoryRef = inventoryItem 
          ? doc(inventoryItemsCollection, inventoryItem.id)
          : doc(inventoryItemsCollection);
          
        console.log("INVENTORY", {
          inventoryItem: inventoryItem?.id || 'nuevo-item',
          associatedProductId: item.id,
          quantity: quantityToRestore
        })
        console.log('Procesando ítem para cancelación:', {
          id: item.id,
          quantity: quantityToRestore
        });
        
        try {
          // Si ya encontramos el item en el estado local, lo actualizamos directamente
          if (inventoryItem) {
            // El ítem ya existe, actualizar cantidad
            const currentQuantity = inventoryItem.quantity || 0;
            const newQuantity = currentQuantity + quantityToRestore;
            
            console.log('Restaurando stock:', {
              itemId: item.id,
              currentQuantity,
              quantityToAdd: quantityToRestore,
              newQuantity
            });
            
            // Actualizar documento en Firestore
            await updateDoc(inventoryRef, {
              quantity: newQuantity,
              lastModifiedTime: getTimestamp()
            });
            
            console.log('Documento de inventario actualizado en Firestore');
            
            // Guardar para actualizar estado local
            inventoryUpdates.push({
              id: inventoryItem.id,
              quantity: newQuantity,
              lastModifiedTime: getTimestamp()
            });
          } else {
            // El ítem no existe en inventario, crear nuevo
            const itemData = state.items?.find(i => i.id === item.id);
            
            if (itemData) {
              // Crear un nuevo ID para el item de inventario
              const newInventoryId = doc(inventoryItemsCollection).id;
              
              const newInventoryItem: InventoryItem = {
                id: newInventoryId,
                associatedProductId: item.id, // Asociamos con el producto
                name: itemData.name,
                type: 'finished' as 'finished', 
                unit: 'units',
                quantity: quantityToRestore,
                minQuantity: 0,
                costPrice: itemData.costPrice || 0,
                lastModifiedTime: getTimestamp(),
                isDeleted: false,
                ownerId: currentUser.uid
              };
              
              // Crear nuevo documento
              await setDoc(inventoryRef, newInventoryItem);
              
              // Guardar para estado local
              inventoryUpdates.push(newInventoryItem);
            }
          }
          
          // Intento simplificado - solo para actualizar el estado local
          try {
            // Para este caso particular, solo actualizamos el estado local sin
            // intentar crear el documento en Firestore para evitar errores de permisos
            
            // Creamos un ID local para el movimiento
            const localId = `cancel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            // Obtenemos el ID del inventario (sea existente o recién creado)
            const targetInventoryId = inventoryItem ? inventoryItem.id : inventoryUpdates[inventoryUpdates.length - 1].id;
            
            // Crear objeto de movimiento para el estado local solamente
            const localMovement = {
              id: localId,
              inventoryItemId: targetInventoryId,
              itemId: item.id,
              quantity: quantityToRestore,
              type: 'adjustment',
              notes: `Restauración por cancelación de orden #${order.orderName || orderId}`,
              reference: orderId,
              date: getTimestamp(),
              ownerId: currentUser.uid,
              parentUserId: state.parentId || undefined,
              createdAt: getTimestamp(),
              createdBy: currentUser.uid
            };
            
            // Guardar en la lista para actualizar el estado local
            createdStockMovements.push(localMovement);
            console.log('Movimiento local creado para estado:', localMovement);
            
            // NOTA: No intentamos crear el documento en Firestore por problemas de permisos
            // Si esto es críticamente necesario, debe revisarse las reglas de seguridad
          } catch (moveError) {
            console.error('Error general al crear movimiento de stock:', moveError);
            // Continuamos con el proceso a pesar de errores en movimientos
          }
        } catch (itemError) {
          console.error(`Error al procesar ítem ${item.id}:`, itemError);
          // Continuamos con los demás items a pesar del error
        }
      }
      
      // Actualizar estado local
      const updatedClosedOrders = [...state.closedOrders];
      updatedClosedOrders[orderIndex] = updatedOrder;
      
      // Preparar actualizaciones de estado
      const updates: any = {
        closedOrders: updatedClosedOrders
      };
      
      // Actualizar inventario en estado local si hay cambios
      if (inventoryUpdates.length > 0) {
        const currentInventory = [...(state.inventoryItems || [])];
        
        inventoryUpdates.forEach((update: any) => {
          const idx = currentInventory.findIndex(item => item.id === update.id);
          if (idx !== -1) {
            currentInventory[idx] = {
              ...currentInventory[idx],
              quantity: update.quantity,
              lastModifiedTime: update.lastModifiedTime
            };
          } else {
            currentInventory.push(update as InventoryItem);
          }
        });
        
        updates.inventoryItems = currentInventory;
      }
      
      // Actualizar movimientos de stock en estado local
      if (createdStockMovements.length > 0) {
        const allStockMovements = [...(state.stockMovements || []), ...createdStockMovements];
        updates.stockMovements = allStockMovements;
      }
      
      console.log('Actualizaciones a realizar en el estado local:', {
        'closedOrders': updatedClosedOrders.length,
        'inventoryItems': inventoryUpdates.length > 0 ? 'Sí' : 'No',
        'stockMovements': createdStockMovements.length
      });
      
      // Actualizar estado con todas las actualizaciones
      updateState(updates);
      
      console.log('Estado actualizado con éxito, orden cancelada:', orderId);
    } catch (error: unknown) {
      console.error('Error al cancelar la orden:', error);
      console.error('Detalles del estado:', {
        'orderIndex': orderIndex,
        'orderExists': state.closedOrders[orderIndex] ? true : false,
        'hasInventoryItems': state.inventoryItems ? true : false,
        'inventoryItemsCount': state.inventoryItems?.length || 0,
        'hasStockMovements': state.stockMovements ? true : false
      });
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      throw new Error(`Error al cancelar la orden: ${errorMessage}`);
    }
  }
});
